/**
 * Resolve a human-readable location for off-site attendance.
 * Uses browser Geolocation + reverse geocoding (Nominatim, optional maps.co).
 */

export const LOCATION_REQUIRED_MSG =
  "Please allow or turn on your location to check in and check out.";

function getCurrentPosition() {
  return new Promise((resolve) => {
    if (!navigator?.geolocation) {
      resolve({ coords: null, error: "unsupported" });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          coords: { lat: pos.coords.latitude, lng: pos.coords.longitude },
          error: null,
        }),
      (err) => {
        const code = err?.code;
        // 1 = PERMISSION_DENIED, 2 = POSITION_UNAVAILABLE, 3 = TIMEOUT
        const error =
          code === 1 ? "denied" : code === 3 ? "timeout" : "unavailable";
        resolve({ coords: null, error });
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
    );
  });
}

async function reverseNominatim(lat, lng) {
  const url = `https://nominatim.openstreetmap.org/reverse?lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lng)}&format=json&addressdetails=1`;
  const res = await fetch(url, {
    headers: {
      Accept: "application/json",
      "User-Agent": "HRMS-Portal/1.0 (attendance-offsite)",
    },
  });
  if (!res.ok) return null;
  const data = await res.json();
  return data.display_name || data.name || null;
}

async function reverseMapsCo(lat, lng) {
  const key = process.env.REACT_APP_MAPS_CO_API_KEY;
  if (!key) return null;
  const url = `https://geocode.maps.co/reverse?lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lng)}&api_key=${encodeURIComponent(key)}`;
  const res = await fetch(url, { headers: { Accept: "application/json" } });
  if (!res.ok) return null;
  const data = await res.json();
  return data.display_name || data.address || null;
}

/**
 * @returns {Promise<{ ok: boolean, text: string, coords: {lat:number,lng:number}|null, error: string|null }>}
 */
export async function resolveAttendanceLocation() {
  const { coords, error } = await getCurrentPosition();
  if (!coords) {
    return {
      ok: false,
      text: "",
      coords: null,
      error: error || "unavailable",
    };
  }

  const { lat, lng } = coords;
  try {
    const nominatim = await reverseNominatim(lat, lng);
    if (nominatim) return { ok: true, text: nominatim, coords, error: null };
  } catch (_) {
    /* try fallback */
  }

  try {
    const mapsCo = await reverseMapsCo(lat, lng);
    if (mapsCo) return { ok: true, text: mapsCo, coords, error: null };
  } catch (_) {
    /* use coordinates */
  }

  return {
    ok: true,
    text: `${Number(lat).toFixed(5)}, ${Number(lng).toFixed(5)}`,
    coords,
    error: null,
  };
}

export function isUsableAttendanceLocation(loc) {
  return Boolean(loc && loc.ok && loc.coords && Number.isFinite(loc.coords.lat) && Number.isFinite(loc.coords.lng));
}
